import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  NgZone,
  OnInit,
  signal,
  ViewChild
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { take } from 'rxjs/operators';
import { IdentityUser, Menu } from '@model/identity';
import { MenuItem } from '@model/identity/menu-item';
import { GetIdentityUserUseCase } from '@usecase/identity';
// Importación modular individual - mejor tree-shaking
import { SuraOptionListComponent } from '@sura-ui/option-list';
import { TopBarComponent } from '@view/shared/organisms/top-bar/top-bar.component';
import { getIdentityDi } from '@cfg/security/identity-di';
import { CommonModule } from '@angular/common';
import { RouteService } from '@helpers/routing/router.service';
import { AppTranslationService } from '@service/translation/app-translation.service';
import { IdentityServiceImpl } from '@adapter/security';
import { AppSplashComponent } from '../splash/splash.component';
import { SuraIconComponent } from '@sura-ui/icon';
import { SuraImageComponent } from '@sura-ui/image';
import { AppDialogService } from '@service/dialog/app-dialog.service';
import { UserProfileDialogComponent } from '@view/shared/organisms/user-profile-dialog/user-profile-dialog.component';
import { APP_VERSION } from 'src/app/common/constants-ejb';

interface SidebarMenuItem {
  id: string;
  title: string;
  icon: string;
  active: boolean;
  clickable: boolean;
  children?: SidebarMenuItem[];
  /** Si viene, el ítem abre esta URL externa en una pestaña nueva en lugar de navegar por router. */
  url?: string;
}

interface SidebarSection {
  title: string;
  /** Ícono PrimeIcons representativo cuando el sidebar está colapsado. */
  icon: string;
  items: SidebarMenuItem[];
}

interface UserMenuItemConfig {
  id: string;
  title: string;
  subtitle?: string;
  icon: string;
  iconPosition?: 'left' | 'right';
  onlyIcon?: boolean;
  active?: boolean;
  clickable?: boolean;
  separator?: boolean;
}

/**
 * Correcciones de `contextPath` para ítems de menú cuyo valor configurado en SEUS
 * no coincide con la ruta real registrada en Angular (routes.ts). Se aplica por
 * nombre del ítem para no afectar el resto del menú, que sí llega correctamente.
 */
const MENU_CONTEXT_PATH_OVERRIDES: Record<string, string> = {
  'Seguimiento de Alertas Tempranas': 'riesgos-potenciales',
  'Alertas Tempranas': 'riesgos-potenciales',
  'Riesgos Potenciales': 'riesgos-potenciales'
};

interface UserMenuItemConfig {
  id: string;
  title: string;
  subtitle?: string;
  icon: string;
  iconPosition?: 'left' | 'right';
  onlyIcon?: boolean;
  active?: boolean;
  clickable?: boolean;
  separator?: boolean;
}

/**
 * Componente que renderiza el layout de la aplicación
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-layout',
  imports: [
    RouterModule,
    CommonModule,
    TopBarComponent,
    SuraOptionListComponent,
    SuraIconComponent,
    SuraImageComponent,
    AppSplashComponent
  ],
  providers: [getIdentityDi],
  template: `
    <div
      class="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors duration-300"
    >
      <section class="fixed top-0 left-0 right-0 z-20">
        <app-top-bar
          [username]="user().fullName || ''"
          [lastAccess]="user().lastAccess || ''"
          (menuStateChange)="onMenuStateChange($event)"
          (userMenuStateChange)="onUserMenuStateChange($event)"
        >
        </app-top-bar>
        <!-- userMenu -->
        <div class="relative">
          <div
            class="absolute right-0 w-52 bg-white dark:bg-gray-800 rounded-lg app-shadow--basic app-animate-all--ease"
            [ngClass]="{
              'top-0 z-10 opacity-100': isUserMenuOpen(),
              'opacity-0 -top-10 -z-10 pointer-events-none': !isUserMenuOpen()
            }"
          >
            <sura-option-list
              [config]="userMenuConfig()"
              (itemClick)="onUserMenuItemClick($event)"
            ></sura-option-list>
          </div>
        </div>
      </section>
      <div class="flex flex-1 relative mt-14">
        <!-- Backdrop móvil -->
        @if (isMobile() && isMenuOpen()) {
          <div
            class="fixed inset-0 top-14 bg-black/50 z-20 transition-opacity duration-300"
            (click)="onMenuStateChange('closed')"
          ></div>
        }
        <!-- sidebar wrapper (fixed al viewport, no crece con el contenido) -->
        <div
          class="fixed left-0 top-14 h-[calc(100vh-3.5rem)] app-animate-all--ease"
          [ngClass]="{
            'w-[264px]': isMenuOpen(),
            'w-20': !isMenuOpen() && !isMobile(),
            'w-0': !isMenuOpen() && isMobile(),
            'z-30': isMobile(),
            'z-10': !isMobile()
          }"
        >
          <!-- Botón flotante colapsar/expandir (fuera del aside para no ser recortado) -->
          <!-- Se muestra siempre, también en móvil, para poder abrir/cerrar el menú -->
          <button
            class="absolute top-7 w-8 h-8 bg-white dark:bg-gray-800 rounded-full shadow-lg border border-gray-200 dark:border-gray-600 flex items-center justify-center z-30 hover:bg-sura-blue-50 dark:hover:bg-gray-700 transition-colors duration-200 cursor-pointer"
            [ngClass]="{ '-right-4': !isMobile(), '-right-8': isMobile() }"
            (click)="onMenuStateChange(isMenuOpen() ? 'closed' : 'opened')"
            [attr.aria-label]="isMenuOpen() ? 'Colapsar menú' : 'Expandir menú'"
          >
            <div
              class="transition-transform duration-300"
              [ngClass]="{ 'rotate-90': isMobile() && isMenuOpen() }"
            >
              <sura-icon
                [config]="{
                  name: isMobile()
                    ? isMenuOpen()
                      ? 'times'
                      : 'align-justify'
                    : isMenuOpen()
                      ? 'chevron-left'
                      : 'chevron-right',
                  size: 'small',
                  clickable: true,
                  color: '#2d6df6'
                }"
              ></sura-icon>
            </div>
          </button>

          <aside
            #sidebar
            class="h-full bg-white dark:bg-gray-900 flex flex-col justify-between overflow-hidden shadow-lg transition-colors duration-300"
          >
            <!-- Sidebar Header -->
            <div
              class="flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex-shrink-0"
              [ngClass]="{ 'justify-center': !isMenuOpen() }"
            >
              <div
                class="flex items-center justify-center w-10 h-10 rounded-lg bg-sura-blue-50 dark:bg-sura-blue-800/30 flex-shrink-0"
              >
                <sura-icon
                  [config]="{
                    name: 'building',
                    size: 'large',
                    color: '#2d6df6'
                  }"
                ></sura-icon>
              </div>
              @if (isMenuOpen()) {
                <div class="flex-1 min-w-0">
                  <p
                    class="text-sm font-bold text-gray-900 dark:text-gray-100 truncate"
                  >
                    Girop
                    <span
                      class="font-normal text-xs text-gray-400 dark:text-gray-500"
                      >v{{ appVersion }}</span
                    >
                  </p>
                  <p
                    class="text-xs text-sura-gray-200 dark:text-gray-400 truncate"
                  >
                    Gestión de servicios
                  </p>
                </div>
              }
            </div>

            <!-- Menu Items (solo esta área hace scroll) -->
            <div class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden py-2">
              <!-- Template recursivo para items del menú (sidebar abierto) -->
              <ng-template #menuItemTpl let-items="items" let-level="level">
                @for (item of items; track item.id) {
                  <div
                    class="relative flex items-center gap-3 cursor-pointer transition-all duration-150 rounded-lg mx-2 mb-0.5"
                    [title]="item.title"
                    [style.padding-left.rem]="
                      isMenuOpen() ? 0.75 + level * 0.75 : 0
                    "
                    [ngClass]="{
                      'py-2.5 pr-3': isMenuOpen(),
                      'px-0 py-2.5 justify-center': !isMenuOpen(),
                      'bg-sura-blue-500 text-white shadow-sm': item.active,
                      'text-gray-700 dark:text-gray-300 hover:bg-sura-blue-50 dark:hover:bg-gray-800':
                        !item.active
                    }"
                    (click)="onMenuItemClicked(item)"
                  >
                    <div
                      class="flex items-center justify-center w-8 h-8 flex-shrink-0"
                      [ngClass]="{
                        'rounded-lg bg-sura-blue-50 dark:bg-sura-blue-800/30':
                          !isMenuOpen() && !item.active
                      }"
                    >
                      <sura-icon
                        [config]="{
                          name: item.icon,
                          size: level > 0 ? 'small' : 'medium',
                          color: item.active ? '#ffffff' : '#2d6df6'
                        }"
                      ></sura-icon>
                    </div>
                    @if (isMenuOpen()) {
                      <span
                        class="text-sm truncate flex-1"
                        [class.font-medium]="
                          item.active || item.children?.length
                        "
                        >{{ item.title }}</span
                      >
                      @if (item.children?.length) {
                        <sura-icon
                          [config]="{
                            name: expandedItems().has(item.id)
                              ? 'chevron-down'
                              : 'chevron-right',
                            size: 'small',
                            color: item.active ? '#ffffff' : '#9ca3af'
                          }"
                          class="flex-shrink-0"
                        ></sura-icon>
                      }
                    }
                  </div>
                  @if (item.children?.length) {
                    @if (isMenuOpen() && expandedItems().has(item.id)) {
                      <!-- Abierto: solo si está expandido manualmente -->
                      <ng-container
                        *ngTemplateOutlet="
                          menuItemTpl;
                          context: { items: item.children, level: level + 1 }
                        "
                      ></ng-container>
                    } @else if (!isMenuOpen()) {
                      <!-- Colapsado: aplanar los hijos como íconos al mismo nivel -->
                      <ng-container
                        *ngTemplateOutlet="
                          menuItemTpl;
                          context: { items: item.children, level: 0 }
                        "
                      ></ng-container>
                    }
                  }
                }
              </ng-template>

              <!-- Template recursivo para popover colapsado -->
              <ng-template #popoverItemTpl let-items="items" let-level="level">
                @for (item of items; track item.id) {
                  @if (item.children?.length) {
                    <p
                      class="py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                      [title]="item.title"
                      [style.padding-left.rem]="1 + level * 0.5"
                    >
                      {{ item.title }}
                    </p>
                    <ng-container
                      *ngTemplateOutlet="
                        popoverItemTpl;
                        context: { items: item.children, level: level + 1 }
                      "
                    ></ng-container>
                  } @else {
                    <div
                      class="py-2 text-sm cursor-pointer transition-colors duration-150"
                      [title]="item.title"
                      [style.padding-left.rem]="1 + level * 0.5"
                      [ngClass]="{
                        'text-sura-blue-500 font-medium bg-sura-blue-50 dark:bg-sura-blue-800/20':
                          item.active,
                        'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700':
                          !item.active
                      }"
                      (click)="onMenuItemClicked(item)"
                    >
                      {{ item.title }}
                    </div>
                  }
                }
              </ng-template>

              @for (section of menuSections(); track si; let si = $index) {
                <div
                  class="relative"
                  (mouseenter)="hoveredSection.set(si)"
                  (mouseleave)="hoveredSection.set(null)"
                >
                  <!-- Título de sección -->
                  @if (section.title) {
                    @if (isMenuOpen()) {
                      <p
                        class="px-4 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mt-3 mb-1"
                      >
                        {{ section.title }}
                      </p>
                    } @else {
                      <!-- Colapsado: ícono representativo de la sección (PrimeIcons via sura-icon) -->
                      <div
                        class="flex items-center justify-center mt-3 mb-1"
                        [title]="section.title"
                      >
                        <div
                          class="flex items-center justify-center w-8 h-8 rounded-lg bg-sura-blue-50 dark:bg-sura-blue-800/30"
                        >
                          <sura-icon
                            [config]="{
                              name: section.icon,
                              size: 'medium',
                              color: '#2d6df6'
                            }"
                          ></sura-icon>
                        </div>
                      </div>
                    }
                  }

                  <!-- Items (template recursivo) - en colapsado se ven como íconos centrados -->
                  <ng-container
                    *ngTemplateOutlet="
                      menuItemTpl;
                      context: { items: section.items, level: 0 }
                    "
                  ></ng-container>

                  <!-- Popover en modo colapsado -->
                  @if (!isMenuOpen() && hoveredSection() === si) {
                    <div
                      class="absolute left-full top-0 ml-1 z-50 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-100 dark:border-gray-700 py-2 min-w-[200px]"
                    >
                      @if (section.title) {
                        <p
                          class="px-4 py-1.5 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider"
                        >
                          {{ section.title }}
                        </p>
                      }
                      <ng-container
                        *ngTemplateOutlet="
                          popoverItemTpl;
                          context: { items: section.items, level: 0 }
                        "
                      ></ng-container>
                    </div>
                  }
                </div>
              }
            </div>

            <!-- Logo SURA + Theme Toggle  (fijo abajo, NO hace scroll) -->
            <div
              class="px-2 py-3 flex-shrink-0 border-t border-gray-100 dark:border-gray-700"
            >
              <div class="flex items-center justify-center mb-2">
                <button (click)="navigate('/dashboard')">
                  <sura-image
                    class="girop-logo-tint"
                    [config]="
                      isMenuOpen()
                        ? {
                            src: 'assets/images/girop-logo.png',
                            alt: 'SURA',
                            width: 130,
                            height: 44
                          }
                        : {
                            src: 'assets/images/menu_closed_sura.png',
                            alt: 'SURA',
                            width: 36,
                            height: 36
                          }
                    "
                  ></sura-image>
                </button>
              </div>
              <div class="flex items-center justify-center">
                <div
                  class="flex items-center bg-gray-100 dark:bg-gray-800 rounded-full p-1 gap-1"
                >
                  <button
                    class="flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200"
                    [ngClass]="{
                      'bg-white text-yellow-500 shadow-sm': !isDarkTheme(),
                      'text-sura-gray-200 dark:text-gray-500': isDarkTheme()
                    }"
                    (click)="toggleTheme(false)"
                    title="Modo claro"
                  >
                    <sura-icon
                      [config]="{ name: 'sun', size: 'medium' }"
                    ></sura-icon>
                  </button>
                  <button
                    class="flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200"
                    [ngClass]="{
                      'bg-sura-blue-500 text-white shadow-sm': isDarkTheme(),
                      'text-sura-gray-200': !isDarkTheme()
                    }"
                    (click)="toggleTheme(true)"
                    title="Modo oscuro"
                  >
                    <sura-icon
                      [config]="{ name: 'moon', size: 'medium' }"
                    ></sura-icon>
                  </button>
                </div>
              </div>
            </div>
          </aside>
        </div>
        <!-- main -->
        <div
          class="flex-1 overflow-y-auto overflow-x-hidden app-animate-all--ease bg-gray-50 dark:bg-gray-950 transition-colors duration-300"
          [ngClass]="{
            'ml-[264px]': isMenuOpen() && !isMobile(),
            'ml-20': !isMenuOpen() && !isMobile(),
            'ml-0': isMobile()
          }"
        >
          <router-outlet></router-outlet>
        </div>
      </div>
    </div>
    <app-splash></app-splash>
  `
})
export class LayoutComponent implements OnInit {
  @ViewChild('sidebar') sidebar?: ElementRef;
  appVersion = APP_VERSION;

  /** caso de uso para obtener el usuario */
  usecase = inject(GetIdentityUserUseCase);
  translator = inject(AppTranslationService);
  router: RouteService = inject(RouteService);
  ngZone = inject(NgZone);
  seusService = inject(IdentityServiceImpl);
  dialogService = inject(AppDialogService);

  /** opciones de menú del usuario */

  userMenu = signal<UserMenuItemConfig[]>([]);
  user = signal<IdentityUser>({} as IdentityUser);
  userMenuState = signal('closed');
  menuSections = signal<SidebarSection[]>([]);
  expandedItems = signal<Set<string>>(new Set());
  menuState = signal('opened');
  isMobile = signal(false);
  isDarkTheme = signal(false);
  hoveredSection = signal<number | null>(null);
  private loadedMenus = signal<Menu[]>([]);

  private destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      const dark = this.isDarkTheme();
      document.documentElement.classList.toggle('dark', dark);
    });
    // Detectar mobile al inicio y en resize
    this.checkMobile();
    const onResize = () => this.checkMobile();
    window.addEventListener('resize', onResize);
    this.destroyRef.onDestroy(() =>
      window.removeEventListener('resize', onResize)
    );
  }

  private checkMobile() {
    const mobile = window.innerWidth < 768;
    this.isMobile.set(mobile);
    if (mobile && this.menuState() === 'opened') {
      this.menuState.set('closed');
    }
  }

  toggleTheme(dark: boolean) {
    this.isDarkTheme.set(dark);
  }

  navigate(url?: string) {
    this.router.navigateTo(url ?? '/');
  }

  toggleExpand(id: string) {
    this.expandedItems.update((set) => {
      const newSet = new Set(set);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  }

  userMenuConfig() {
    return {
      items: this.userMenu(),
      selectable: true
    };
  }

  /**
   * Inicialización del componente, se obtiene el usuario y se construye el menú
   */
  ngOnInit(): void {
    this.seusService
      .getUserInfo()
      .pipe(take(1))
      .subscribe({
        next: (user) => {
          this.user.set(user);
          this.prepareUserMenu(user);
          this.seusService
            .getMenuFromApi()
            .pipe(take(1))
            .subscribe({
              next: (menus) => {
                this.loadedMenus.set(menus);
                this.prepareAppMenuFromMenus(menus);
              },
              error: () => this.menuSections.set([])
            });
          // Obtener última conexión del usuario
          this.seusService.getUserLastAccess().then((lastAccess) => {
            this.user.update((u) => ({
              ...u,
              lastAccess: new Date(lastAccess).toLocaleString('es-CO', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
              })
            }));
          });
        },
        error: () => {
          this.user.set({} as IdentityUser);
          this.menuSections.set([]);
          this.userMenu.set([]);
        }
      });

    this.ngZone.runOutsideAngular(() => {
      const elem = this.sidebar?.nativeElement as HTMLElement;
      if (elem) {
        elem.addEventListener('mouseenter', () => {
          this.onMenuStateChange('opened');
        });
        elem.addEventListener('mouseleave', () => {
          this.onMenuStateChange('closed');
        });
      }
    });
  }

  /**
   * Se ejecuta cuando el estado del menú cambia
   * @param event puede ser opened o closed
   */
  onMenuStateChange(event: string) {
    this.menuState.set(event);
  }

  /**
   * Se ejecuta cuando el estado del menú de usuario cambia
   * @param event puede ser opened o closed
   */
  onUserMenuStateChange(event: string) {
    this.userMenuState.set(event);
  }

  /**
   * Se ejecuta cuando se hace click en un item del menú de usuario
   * @param item item del menú
   */
  onUserMenuItemClick(item: UserMenuItemConfig) {
    this.userMenuState.set('closed');
    if (item.id === 'logout') {
      this.seusService.logout();
    } else if (item.id === 'profile') {
      this.dialogService.show(UserProfileDialogComponent, {
        user: this.user()
      });
    } else {
      console.warn('Opción de menú no soportada:', item);
    }
  }

  /**
   * Se ejecuta cuando se hace click en un item del menú sidebar
   * @param item item del menú
   */
  onMenuItemClicked(item: SidebarMenuItem) {
    if (item.children?.length) {
      this.toggleExpand(item.id);
      return;
    }

    if (item.url) {
      if (this.isSafeExternalUrl(item.url)) {
        window.open(item.url, '_blank', 'noopener,noreferrer');
      } else {
        console.warn(
          '[layout] URL externa rechazada por esquema no permitido:',
          item.url
        );
      }
      if (this.isMobile()) {
        this.menuState.set('closed');
      }
      return;
    }

    // En móvil, cerrar sidebar al navegar
    if (this.isMobile()) {
      this.menuState.set('closed');
    }

    this.menuSections.update((sections) =>
      sections.map((section) => ({
        ...section,
        items: this.setActiveRecursive(section.items, item.id)
      }))
    );

    const found = this.findMenuItemById(item.id);
    if (found) {
      const contextPath =
        MENU_CONTEXT_PATH_OVERRIDES[found.name] ?? found.contextPath;
      this.router.navigateTo(`/${contextPath}`);
    }
  }

  private setActiveRecursive(
    items: SidebarMenuItem[],
    activeId: string
  ): SidebarMenuItem[] {
    return items.map((mi) => ({
      ...mi,
      active: mi.id === activeId,
      children: mi.children
        ? this.setActiveRecursive(mi.children, activeId)
        : undefined
    }));
  }

  /** Solo se permiten enlaces externos con esquema http(s) para evitar javascript:/data:/file:. */
  private isSafeExternalUrl(raw: string): boolean {
    try {
      const url = new URL(raw);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  }

  private findMenuItemById(id: string): MenuItem | undefined {
    const search = (items: MenuItem[]): MenuItem | undefined => {
      for (const mi of items) {
        if (mi.cdresource.toString() === id) return mi;
        if (mi.children?.length) {
          const found = search(mi.children);
          if (found) return found;
        }
      }
      return undefined;
    };

    for (const menu of this.loadedMenus()) {
      const found = search(menu.menuItems);
      if (found) return found;
    }
    return undefined;
  }

  /**
   * Determina si el menú de usuario está abierto
   */
  isUserMenuOpen = computed(() => this.userMenuState() === 'opened');

  isMenuOpen = computed(() => this.menuState() === 'opened');

  /**
   * Construye el menú de la aplicación desde Menu[]
   * Estructura API: Menu > menuItems[Girop] > children[Administración, Servicios] > children[items]
   * Soporta anidamiento profundo: si un hijo tiene sub-hijos, se aplanan recursivamente como items de la sección
   */
  private prepareAppMenuFromMenus(menus: Menu[]) {
    const sections: SidebarSection[] = [];

    for (const menu of menus) {
      for (const topItem of menu.menuItems) {
        if (topItem.children?.length) {
          for (const section of topItem.children) {
            const items = this.convertMenuItems(section.children || []);
            sections.push({
              title: section.name,
              icon: section.image || this.guessSectionIcon(section.name),
              items
            });
          }
        } else {
          sections.push({
            title: '',
            icon: topItem.image || 'bars',
            items: [
              {
                id: topItem.cdresource.toString(),
                title: topItem.name,
                icon: topItem.image || 'bars',
                active: false,
                clickable: true
              }
            ]
          });
        }
      }
    }

    this.menuSections.set(sections);
  }

  /**
   * Heurística para sugerir un ícono PrimeIcons cuando la API no envía `image`
   * en la sección del menú. Permite ver íconos en el sidebar colapsado.
   */
  private guessSectionIcon(title: string): string {
    const t = (title || '').toLowerCase();
    if (t.includes('admin')) return 'cog';
    if (t.includes('servicio')) return 'briefcase';
    if (t.includes('reporte')) return 'chart-bar';
    if (t.includes('cumplim')) return 'check-square';
    if (t.includes('agenda') || t.includes('cita')) return 'calendar';
    if (t.includes('persona') || t.includes('usuario')) return 'user';
    if (t.includes('empresa') || t.includes('cliente')) return 'users';
    if (t.includes('factur')) return 'dollar';
    if (t.includes('informe')) return 'file';
    return 'folder';
  }

  /**
   * Convierte recursivamente MenuItem[] a SidebarMenuItem[] preservando la jerarquía padre-hijo.
   * Si el recurso viene marcado con target `_blank`, `contextPath` se trata como URL externa.
   */
  private convertMenuItems(items: MenuItem[]): SidebarMenuItem[] {
    return items.map((item) => {
      const isExternal = item.target === '_blank' && !!item.contextPath;
      return {
        id: item.cdresource.toString(),
        title: item.name,
        icon: item.image || 'bars',
        active: false,
        clickable: !item.children?.length,
        children: item.children?.length
          ? this.convertMenuItems(item.children)
          : undefined,
        url: isExternal ? item.contextPath : undefined
      };
    });
  }

  private prepareUserMenu(user: IdentityUser) {
    this.userMenu.set([
      {
        id: 'profile',
        title: 'Mi perfil',
        icon: 'user',
        clickable: true
      },
      {
        id: 'logout',
        title: 'Cerrar sesión',
        icon: 'sign-out',
        clickable: true,
        separator: true
      }
    ]);
  }
}
